import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, timeout} from 'rxjs';

import {environment} from '../../../environments/environment';
import {skipForbiddenRedirect} from '../../interceptors/forbidden-redirect.context';
import {
  AccountEsistente,
  CollegamentoEsitoDTO,
  CollegamentoStatoDTO,
  CollegamentoStatoDTOProviderEnum,
  CollegamentoStatoDTOStatoEnum,
  CollegamentoTokenForm,
  SegnalazioneAccountForm,
  SegnalazioneAccountInviataDTO,
} from '../../core/api/generated';

/**
 * Client HTTP diretto (`HttpClient`) per gli endpoint pubblici di collegamento identità SPID/CIE →
 * account esistente. I tipi di richiesta/risposta derivano dai DTO del client OpenAPI generato
 * (`CollegamentoAccountService` in `src/app/core/api/generated`, che resta inutilizzato qui per i
 * motivi sotto).
 *
 * Gli endpoint sono pubblici e NON vogliono l'header `Authorization`: il token dell'identità
 * temporanea viaggia nel body. Qui si usa `HttpClient` direttamente (niente `Configuration`
 * del client generato, che aggiungerebbe il Bearer) e nessun interceptor ne aggiunge uno.
 * I path non rientrano nelle skip list di `RefreshTokenInterceptor`/`Forbidden403Interceptor`
 * (nessun segmento login|auth|logout|token|passkey), ma il BE non risponde mai 401/403 qui.
 */

export const COLLEGAMENTO_PATH = '/api/v1/account/collegamento-identita';

/**
 * Tempo massimo di ogni chiamata. Allo scadere l'osservabile va in errore (`TimeoutError`, senza
 * status → classificato `rete`) e la modale mostra «Riprova»: l'utente non resta mai bloccato.
 */
export const COLLEGAMENTO_TIMEOUT_MS = 20_000;

export type StatoCollegamento = `${CollegamentoStatoDTOStatoEnum}`;
export type ProviderIdentita = `${CollegamentoStatoDTOProviderEnum}`;

/**
 * Risposta di `/stato`: DTO generato `CollegamentoStatoDTO` con `stato` sempre presente
 * (nel DTO è opzionale) e stato/provider come unioni di letterali derivate dagli enum
 * generati. `account.email_mascherata` può essere `null`: `AccountEsistente` è un record
 * annidato senza `@JsonInclude`, quindi il BE serializza il null (ricade nel campo `string`
 * del generato).
 */
export type CollegamentoStatoResponse = Omit<CollegamentoStatoDTO, 'stato' | 'provider' | 'account'> & {
  stato: StatoCollegamento;
  provider?: ProviderIdentita;
  account?: Omit<AccountEsistente, 'email_mascherata'> & {email_mascherata?: string | null};
};

/** Esito di `/collega`: il BE valorizza sempre entrambi i booleani (primitivi nel record). */
export type CollegamentoCollegaResponse = Required<CollegamentoEsitoDTO>;

/** Body di `/segnalazione`. `testo_segnalazione` è obbligatorio (`@NotBlank` nel BE). */
export type CollegamentoSegnalazioneRequest = SegnalazioneAccountForm;

/** Esito di `/segnalazione`: `riferimento` e `gia_inviata` sono sempre valorizzati dal BE. */
export type CollegamentoSegnalazioneResponse = Required<SegnalazioneAccountInviataDTO>;

/** Esito di errore classificato, indipendente dallo status HTTP grezzo. */
export type CollegamentoErrore =
  | 'non_piu_necessario'
  | 'segnalazione_gia_inviata'
  | 'token_non_valido'
  | 'autenticazione_scaduta'
  | 'non_possibile'
  | 'troppe_richieste'
  | 'keycloak_non_disponibile'
  | 'validazione'
  | 'rete'
  | 'altro';

/**
 * Classifica un errore HTTP dei tre endpoint. Si guarda prima `error.key` (contratto del BE),
 * poi lo status come ripiego: così un 503/429 senza body leggibile resta riconoscibile e una
 * `key` sconosciuta con status 4xx non viene scambiata per un errore di rete.
 */
export function classificaErroreCollegamento(err: unknown): CollegamentoErrore {
  const e = err as Partial<HttpErrorResponse> | null | undefined;
  const status = e?.status;
  const key = (e?.error as {key?: string} | null | undefined)?.key;

  switch (key) {
    case 'collegamento_non_piu_necessario':
      return 'non_piu_necessario';
    case 'segnalazione_account_gia_inviata':
      return 'segnalazione_gia_inviata';
    case 'collegamento_token_non_valido':
      return 'token_non_valido';
    case 'collegamento_autenticazione_scaduta':
      return 'autenticazione_scaduta';
    case 'collegamento_non_possibile':
      return 'non_possibile';
    case 'collegamento_troppe_richieste':
      return 'troppe_richieste';
    case 'collegamento_keycloak_non_disponibile':
      return 'keycloak_non_disponibile';
  }

  if (status === 0 || status === undefined) return 'rete';
  if (status === 429) return 'troppe_richieste';
  if (status === 503) return 'keycloak_non_disponibile';
  if (status === 400) return 'validazione';
  return 'altro';
}

@Injectable({providedIn: 'root'})
export class CollegamentoAccountApiService {
  constructor(private readonly http: HttpClient) {}

  private url(suffix: string): string {
    return `${environment.servicesUrl}${COLLEGAMENTO_PATH}${suffix}`;
  }

  stato(accessToken: string): Observable<CollegamentoStatoResponse> {
    const body: CollegamentoTokenForm = {access_token: accessToken};
    return this.http.post<CollegamentoStatoResponse>(
      this.url('/stato'),
      body,
      {context: skipForbiddenRedirect()},
    ).pipe(timeout(COLLEGAMENTO_TIMEOUT_MS));
  }

  collega(accessToken: string): Observable<CollegamentoCollegaResponse> {
    const body: CollegamentoTokenForm = {access_token: accessToken};
    return this.http.post<CollegamentoCollegaResponse>(
      this.url('/collega'),
      body,
      {context: skipForbiddenRedirect()},
    ).pipe(timeout(COLLEGAMENTO_TIMEOUT_MS));
  }

  /** 201 → nuova segnalazione, 200 → già inviata (`gia_inviata: true`). */
  segnala(body: CollegamentoSegnalazioneRequest): Observable<CollegamentoSegnalazioneResponse> {
    return this.http.post<CollegamentoSegnalazioneResponse>(this.url('/segnalazione'), body, {
      context: skipForbiddenRedirect(),
    }).pipe(timeout(COLLEGAMENTO_TIMEOUT_MS));
  }
}
